import { alpha, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode, useContext } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { IconTile } from 'src/components/IconTile';
import { ComposerBannersContext } from 'src/pages/Conversations/Chat/Banners/composer-banners-context';
import {
  StatePanelActions,
  StatePanelRoot,
  StatePanelText,
  StatePanelTone,
} from './conversation-state-panel.styles';

const dangerTileSx: SxProps<Theme> = (theme) => ({
  backgroundColor: alpha(theme.palette.custom.OV.sys.error, 0.2),
  color: theme.palette.custom.OV.sys.error,
});

interface ConversationStatePanelProps {
  /** lucide icon name */
  icon: string;
  tone?: StatePanelTone;
  title: string;
  description?: string;
  actions?: ReactNode;
  'data-testid'?: string;
}

export const ConversationStatePanel = ({
  icon,
  tone = 'neutral',
  title,
  description,
  actions,
  'data-testid': testId,
}: ConversationStatePanelProps) => {
  // Rendered in the composer dock, under the same banners as the card; outside the dock
  // (tests) there is no banner to square up against.
  const hasBannerAbove = useContext(ComposerBannersContext)?.hasAnyBanner ?? false;

  return (
    <StatePanelRoot role="status" data-testid={testId} hasBannerAbove={hasBannerAbove}>
      <IconTile size="medium" sx={tone === 'danger' ? dangerTileSx : undefined}>
        <DynamicIcon name={icon} library="lucide" />
      </IconTile>
      <StatePanelText>
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: '0.875rem',
            fontWeight: 650,
            color: theme.palette.custom.OV.sys.onBackground,
          })}
        >
          {title}
        </Typography>
        {description ? (
          <Typography
            component="span"
            sx={(theme) => ({
              fontSize: '0.78125rem',
              lineHeight: 1.45,
              color: theme.palette.custom.OV.sys.onSurfaceVariant,
            })}
          >
            {description}
          </Typography>
        ) : null}
      </StatePanelText>
      {actions ? <StatePanelActions>{actions}</StatePanelActions> : null}
    </StatePanelRoot>
  );
};
